// Answer library actions with their toasts: save a card's answer (Undo takes it out again) and
// delete one (Undo brings it back). Both are local to MeeMail; nothing here touches Gmail.
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { SavedAnswerView } from "@shared/api";
import { answersApi } from "@web/lib/answersApi";
import { answersKeys } from "@web/lib/answersQueries";
import { registerUndo } from "@web/lib/undoRegistry";

const refresh = (queryClient: QueryClient) =>
  void queryClient.invalidateQueries({ queryKey: answersKeys.all });

/** Saves the research answer on a card. Returns false when it could not be saved. */
export async function saveCardAnswer(queryClient: QueryClient, cardId: string): Promise<boolean> {
  try {
    const { answer, created } = await answersApi.saveFromCard(cardId);
    refresh(queryClient);
    if (!created) {
      toast("Already in your saved answers.");
      return true;
    }
    registerUndo({
      message: "Saved to your answers.",
      undo: async () => {
        await answersApi.remove(answer.id);
        refresh(queryClient);
      },
      doneMessage: "Taken out of your answers.",
    });
    return true;
  } catch {
    toast("Couldn't save this answer just now. It's still on the card.");
    return false;
  }
}

/** Deletes an answer at once (it leaves the list), with Undo to bring it back. */
export async function deleteAnswer(
  queryClient: QueryClient,
  answer: SavedAnswerView,
): Promise<void> {
  const lists = queryClient.getQueriesData<SavedAnswerView[]>({ queryKey: answersKeys.all });
  queryClient.setQueriesData<SavedAnswerView[]>({ queryKey: answersKeys.all }, (list) =>
    list?.filter((a) => a.id !== answer.id),
  );
  try {
    await answersApi.remove(answer.id);
  } catch {
    for (const [key, data] of lists) queryClient.setQueryData(key, data);
    toast("Couldn't delete that just now. Nothing changed.");
    return;
  }
  refresh(queryClient);
  registerUndo({
    message: `Deleted “${answer.title}”.`,
    undo: async () => {
      await answersApi.restore(answer.id);
      refresh(queryClient);
    },
    doneMessage: "The answer is back.",
  });
}
